import { useState } from 'react'
import { FieldEditorPopover } from '../editors/FieldEditorPopover'
import { pickEditorOptions, EDITOR_TITLES } from '../editable/editorOptions'

/**
 * Les réglages avancés d'un bloc posé DANS un gabarit (colonne, grille).
 *
 * Un bloc de premier niveau ouvre les siens depuis `BuilderBlockWrapper`, qui
 * tient déjà le popover ancré et le commit. Une cellule, elle, a son propre
 * chrome (le wrapper y est `nested` : il ne rend aucune barre) et son propre
 * chemin d'écriture — `foldIntoCell`, la donnée de la cellule étant repliée dans
 * le champ `cells` du contenant. Ce crochet fournit aux deux gabarits la moitié
 * qui manque : la MÊME pop-in ancrée, branchée sur leur commit à eux.
 *
 *   const { openCellSetting, cellSettingPopover } = useCellSettings()
 *   …
 *   <BlockSettingsMenu settings={def.settings} triggerClassName={CELL_CHROME_TRIGGER}
 *     onPick={(decl, anchorEl) => openCellSetting(decl, anchorEl, {
 *       value: cell.data?.[decl.field] ?? def.defaultData?.[decl.field],
 *       commit: (key, value) => foldIntoCell(i, key, value),
 *     })} />
 *   …
 *   {cellSettingPopover}
 */
export const useCellSettings = () => {
  const [target, setTarget] = useState(null)

  const openCellSetting = (decl, anchorEl, { value, commit }) => {
    const type = decl.type ?? decl.editorType
    if (!type || !decl.field) return
    setTarget({
      anchorEl,
      value,
      commit,
      // Même descripteur que celui qu'un champ du rendu porterait dans le DOM :
      // la pop-in ne sait pas d'où vient la déclaration.
      field: {
        ...pickEditorOptions(decl),
        key: decl.field,
        type,
        editorType: type,
        label: decl.label ?? EDITOR_TITLES[type] ?? decl.field,
      },
    })
  }

  const cellSettingPopover = target ? (
    <FieldEditorPopover
      anchorEl={target.anchorEl}
      field={target.field}
      value={target.value ?? ''}
      onSave={(key, value) => { target.commit(key, value); setTarget(null) }}
      onClose={() => setTarget(null)}
    />
  ) : null

  return { openCellSetting, cellSettingPopover }
}
